import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { fl } from "@/lib/finI18n";
import { MasterImportModal } from "@/components/MasterImportModal";
import { useTableTools, TableToolbar, SelectAllBox, SelectBox, deleteEach } from "@/components/TableTools";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Upload, Landmark, Wallet } from "lucide-react";

const CURRENCIES = ["IDR", "USD", "SGD", "EUR", "CNY", "JPY", "AUD", "MYR"];
const GENERIC = new Set(["PT", "CV", "TBK", "UD", "PD", "PERSERO", "BANK", "KAS", "CASH", "REKENING", "REK", "AKUN", "ACCOUNT"]);
const words = (s) => String(s || "").toUpperCase().match(/[A-Z0-9]+/g) || [];

// Mirrors the backend formula: BANK(3)-CURRENCY-ACCOUNT NAME(3), cash boxes use KAS.
export function bankCode(b) {
  const cur = (b.currency || "IDR").toUpperCase().slice(0, 3);
  let seg1 = "KAS";
  if (b.kind !== "cash") {
    const w = words(b.bank_name).filter((x) => !["PT", "TBK", "PERSERO"].includes(x));
    const rest = w.filter((x) => !GENERIC.has(x));
    const r = rest.length ? rest : w;
    seg1 = w.length >= 3 ? w.slice(0, 3).map((x) => x[0]).join("") : (r[0] ? r[0].slice(0, 3) : "BNK");
  }
  const drop = new Set([...GENERIC, cur, seg1, ...words(b.bank_name)]);
  const w = words(b.name);
  const rest = w.filter((x) => !drop.has(x));
  const r = rest.length ? rest : w;
  return `${seg1}-${cur}-${r[0] ? r[0].slice(0, 3) : "XXX"}`;
}

export function BankTab({ clientId, items, onDone }) {
  const { t, lang } = useI18n();
  const [edit, setEdit] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const kindLbl = (b) => fl(b.kind === "cash" ? "bkCash" : "bkBank", lang);
  const tools = useTableTools(items, { date: (r) => r.created_at, text: (b) => `${b.code} ${b.name} ${b.bank_name} ${b.account_number} ${b.currency} ${kindLbl(b)}` });
  const del = async (b) => {
    if (!window.confirm(`Hapus ${b.code || b.name}?`)) return;
    try { await api.delete(`/banks/${b.id}`); toast.success("Dihapus"); onDone(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const bulkDel = async (ids) => { const r = await deleteEach(ids, (id) => api.delete(`/banks/${id}`)); onDone(); return r; };
  const cols = [[fl("bkCode", lang), "code"], [fl("bkName", lang), "name"], [fl("bkKind", lang), kindLbl], [fl("bkBankName", lang), "bank_name"],
    [fl("bkAccNo", lang), "account_number"], [fl("bkCurrency", lang), "currency"]];

  return (
    <Card className="p-4" data-testid="bank-tab">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h2 className="font-head font-semibold">{fl("bkTitle", lang)} <span className="text-muted-foreground font-normal text-sm">({tools.view.length})</span></h2>
        <TableToolbar tools={tools} id="md-banks" cols={cols} filename="bank_kas" onDelete={bulkDel}>
          <Button variant="outline" size="sm" className="h-9" onClick={() => setImportOpen(true)} data-testid="md-import-banks"><Upload className="h-4 w-4 mr-1" />Import</Button>
          <Button size="sm" className="h-9" onClick={() => setEdit({})} data-testid="md-add-banks"><Plus className="h-4 w-4 mr-1" />{t("common.add")}</Button>
        </TableToolbar>
      </div>
      <div className="overflow-auto rounded-md border border-border">
        <Table>
          <TableHeader><TableRow>
            <TableHead className="w-8"><SelectAllBox tools={tools} id="md-banks" /></TableHead>
            {cols.map(([l]) => <TableHead key={l}>{l}</TableHead>)}<TableHead className="text-right">{t("common.actions")}</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {tools.view.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">{t("common.noData")}</TableCell></TableRow>}
            {tools.view.map((b) => (
              <TableRow key={b.id} data-testid={`md-row-banks-${b.id}`}>
                <TableCell><SelectBox tools={tools} row={b} id="md-banks" /></TableCell>
                <TableCell><Badge variant="outline" className="num text-[11px]" data-testid={`bank-code-${b.id}`}>{b.code || "—"}</Badge></TableCell>
                <TableCell className="text-sm font-medium">{b.name}</TableCell>
                <TableCell className="text-xs"><span className="inline-flex items-center gap-1">{b.kind === "cash" ? <Wallet className="h-3.5 w-3.5" /> : <Landmark className="h-3.5 w-3.5" />}{kindLbl(b)}</span></TableCell>
                <TableCell className="text-xs">{b.bank_name || "—"}</TableCell>
                <TableCell className="num text-xs">{b.account_number || "—"}</TableCell>
                <TableCell className="text-xs">{b.currency || "IDR"}</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEdit(b)} data-testid={`md-edit-banks-${b.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => del(b)} data-testid={`md-del-banks-${b.id}`}><Trash2 className="h-4 w-4" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {edit && <BankForm clientId={clientId} item={edit} onClose={() => setEdit(null)} onDone={onDone} />}
      <MasterImportModal open={importOpen} onOpenChange={setImportOpen} clientId={clientId} type="banks" label={fl("bkTitle", lang)} onImported={onDone} />
    </Card>
  );
}

function BankForm({ clientId, item, onClose, onDone }) {
  const { t, lang } = useI18n();
  const [f, setF] = useState({ name: "", kind: "bank", bank_name: "", account_number: "", currency: "IDR", rate: 1 });
  useEffect(() => { setF({ name: item.name || "", kind: item.kind || "bank", bank_name: item.bank_name || "", account_number: item.account_number || "", currency: item.currency || "IDR", rate: item.rate || 1 }); }, [item]);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v?.target ? v.target.value : v }));
  const isBank = f.kind === "bank";
  const save = async () => {
    if (!f.name.trim() || (isBank && (!f.bank_name.trim() || !f.account_number.trim()))) { toast.error(`${isBank ? `${fl("bkName", lang)}, ${fl("bkBankName", lang)}, ${fl("bkAccNo", lang)}` : fl("bkName", lang)} ${fl("bkRequired", lang)}`); return; }
    const payload = { client_id: clientId, ...f, rate: Number(f.rate || 1), ...(isBank ? {} : { account_number: "", bank_name: "" }) };
    try {
      if (item.id) await api.put(`/banks/${item.id}`, payload); else await api.post("/banks", payload);
      toast.success(t("common.saved")); onClose(); onDone();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const req = <span className="text-rose-600">*</span>;
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="bank-form">
        <DialogHeader><DialogTitle>{item.id ? t("common.edit") : t("common.add")} · {fl("bkTitle", lang)}</DialogTitle>
          <DialogDescription className="text-xs">{fl("bkCodeHint", lang)}</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2 space-y-1.5"><Label>{fl("bkName", lang)} {req}</Label><Input value={f.name} onChange={set("name")} data-testid="field-name" /></div>
          <div className="space-y-1.5"><Label>{fl("bkKind", lang)} {req}</Label>
            <Select value={f.kind} onValueChange={set("kind")}>
              <SelectTrigger data-testid="field-kind"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="bank">{fl("bkBank", lang)}</SelectItem><SelectItem value="cash">{fl("bkCash", lang)}</SelectItem></SelectContent>
            </Select></div>
          <div className="space-y-1.5"><Label>{fl("bkCurrency", lang)} {req}</Label>
            <Select value={f.currency} onValueChange={set("currency")}>
              <SelectTrigger data-testid="field-currency"><SelectValue /></SelectTrigger>
              <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select></div>
          {isBank && <>
            <div className="space-y-1.5"><Label>{fl("bkBankName", lang)} {req}</Label><Input value={f.bank_name} onChange={set("bank_name")} placeholder="BCA / Mandiri / BNI" data-testid="field-bank_name" /></div>
            <div className="space-y-1.5"><Label>{fl("bkAccNo", lang)} {req}</Label><Input value={f.account_number} onChange={set("account_number")} data-testid="field-account_number" /></div>
          </>}
          <div className="col-span-2 rounded-md border border-dashed border-primary/40 bg-primary/5 p-3 flex items-center justify-between gap-2">
            <span className="text-xs text-muted-foreground">{fl("bkCode", lang)}</span>
            <span className="num font-semibold text-primary" data-testid="bank-code-preview">{f.name ? bankCode(f) : "—"}</span>
          </div>
        </div>
        <DialogFooter><Button onClick={save} data-testid="md-save">{t("common.save")}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
